import { useEffect, useRef, useState, type JSX } from 'react';
import { Keypad } from './Keypad';
import { parseToNumber } from './keypad-state';
import { usePreferencesStore } from '@client/stores/preferences';
import { useKeypadOverlayStore } from '@client/stores/keypad-overlay';
import { scrollFocusedToUpperThird } from '@client/lib/scroll-into-focus';

export type NumericInputProps = {
  value: number;
  onValueChange: (next: number) => void;
  label: string;
  placeholder?: string | undefined;
  decimal?: boolean | undefined;
  modified?: boolean | undefined;
  onReset?: (() => void) | undefined;
  id?: string | undefined;
};

export function NumericInput({
  value,
  onValueChange,
  label,
  placeholder,
  decimal = true,
  modified = false,
  onReset,
  id,
}: NumericInputProps): JSX.Element {
  const keypadEnabled = usePreferencesStore((s) => s.keypadEnabled);
  const overlayOpen = useKeypadOverlayStore((s) => s.open);
  const overlayClose = useKeypadOverlayStore((s) => s.close);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string>(formatForInput(value, decimal));
  // "pristine" mirrors the standard select-all-on-focus behavior of native
  // inputs. While true, the prefilled rate is shown but the next digit/decimal
  // press REPLACES the value (not appends), and backspace clears it. The user
  // never has to manually wipe a long stale rate (e.g. ₹74,500) before typing
  // the new one. Cleared the moment the user has interacted, so subsequent
  // presses behave as normal append-style editing.
  const [pristine, setPristine] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const display = draft || formatForInput(value, decimal);

  const commitDraft = (text: string) => {
    setPristine(false);
    setDraft(text);
    onValueChange(parseToNumber(text));
  };

  // Tell the global overlay store + scroll the focused input toward the upper
  // third of the visible viewport so the user can see digits as they tap. The
  // visualViewport-aware helper accounts for the on-screen keypad shrinking the
  // visible area on its own — no manual keypad-height constant needed.
  useEffect(() => {
    if (!keypadEnabled) return;
    if (!open) return;
    // Re-sync draft from the latest prop value on each open so a rate that
    // changed while the keypad was closed (e.g. material switch updated
    // ratePaise) shows up correctly when the user re-focuses.
    setDraft(formatForInput(value, decimal));
    setPristine(true);
    overlayOpen();
    const input = inputRef.current;
    // Capture the cancel handle so a fast Done-tap (within 250ms of focus)
    // doesn't trigger a phantom scrollBy after the keypad has already closed.
    const cancelScroll = input ? scrollFocusedToUpperThird(input) : undefined;
    return () => {
      cancelScroll?.();
      overlayClose();
    };
    // value/decimal intentionally not in deps: we only want this to run on
    // open transitions, not on every value change while the keypad is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, keypadEnabled, overlayOpen, overlayClose]);

  const inputProps = keypadEnabled
    ? {
        readOnly: true,
        inputMode: 'none' as const,
        onFocus: () => setOpen(true),
        onClick: () => setOpen(true),
      }
    : {
        inputMode: (decimal ? 'decimal' : 'numeric') as 'decimal' | 'numeric',
        // Native soft keyboard path: select-all on focus so the user can
        // immediately overtype the prefilled rate. requestAnimationFrame
        // dodges Safari/iOS quirk where calling .select() inside a synchronous
        // focus handler is silently ignored on the first tap.
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          const target = e.target;
          requestAnimationFrame(() => target.select());
        },
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const sanitized = e.target.value
            .replace(decimal ? /[^\d.]/g : /\D/g, '')
            .replace(/(\..*)\./g, '$1');
          commitDraft(sanitized);
        },
      };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
          {modified && (
            <span
              aria-label="Modified from default"
              className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500"
            />
          )}
        </label>
        {modified && onReset && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-slate-500 underline-offset-2 hover:underline"
          >
            Reset
          </button>
        )}
      </div>
      <input
        id={id}
        ref={inputRef}
        type="text"
        value={display}
        placeholder={placeholder}
        autoComplete="off"
        // Pristine state mimics ::selection — bg highlight signals "type
        // anything to replace", matching the native select-all-on-focus
        // pattern users already know from desktop browsers / iOS Safari.
        className={`h-12 rounded-xl border px-4 text-right text-lg outline-none transition-colors ${
          pristine && open
            ? 'border-slate-900 bg-slate-900/10 dark:border-slate-100 dark:bg-slate-100/10'
            : 'border-slate-300 focus:border-slate-900 dark:border-slate-700 dark:focus:border-slate-100'
        }`}
        {...inputProps}
      />
      {keypadEnabled && open && (
        <Keypad
          // Empty draft while pristine so Keypad's appendDigit('', '5') = '5'
          // (replaces) instead of appendDigit('74500', '5') = '745005'
          // (appends). First press flips pristine off via commitDraft.
          value={pristine ? '' : draft}
          onChange={commitDraft}
          onClose={() => setOpen(false)}
          decimal={decimal}
        />
      )}
    </div>
  );
}

function formatForInput(value: number, decimal: boolean): string {
  if (value === 0) return '';
  if (!decimal) return String(Math.trunc(value));
  return String(value);
}
